import { produce } from 'immer';
import { createSelector } from 'reselect';
import { find, sortBy } from 'lodash-es';
import { asyncAction } from './async-action';
import { ScopedAsyncType, ScopedType } from './scoped-type';
import * as api from '../../lib/api';
import { pollingAction } from './polling-action';

const initialState = {
  brands: [],
  loading: false,
  productAssignments: [],
  saving: false,
};

const SLICE = 'product-placement-vpi';
const asyncType = ScopedAsyncType(SLICE);
const Type = ScopedType(SLICE);

const CREATE_VPI_SUMMARY = asyncType('CREATE_VPI_SUMMARY');
const LOAD_BRANDS = asyncType('LOAD_BRANDS');
const CREATE_VPI_RECOMMENDATION = asyncType('CREATE_VPI_RECOMMENDATION');
const SET_FIELD_PRODUCT = Type('SET_FIELD_PRODUCT');
const SET_ASSIGNMENT_OPEN = Type('SET_ASSIGNMENT_OPEN');
const SET_RECOMMENDATION_OPEN = Type('SET_RECOMMENDATION_OPEN');
const LOAD_ASSIGNMENTS = asyncType('LOAD_ASSIGNMENTS');

export const selectors = {
  loading: (state) => state[SLICE].loading,
  loadingBrands: (state) => state[SLICE].loadingBrands,
  brands: (state) => state[SLICE].brands,
  saving: (state) => state[SLICE].saving,
  hasFailed: (state) => state[SLICE].hasFailed,
  saveComplete: (state) => state[SLICE].saveComplete,
  productAssignments: (state) => state[SLICE].productAssignments,
  isAssignmentOpen: (state) => state[SLICE].isAssignmentOpen,
  isRecommendationOpen: (state) => state[SLICE].isRecommendationOpen,
  isAssignmentValid: createSelector(
    (state) => state[SLICE].productAssignments,
    (state) => state.fields.selectedGrowerFields,
    (productAssignments, fields) =>
      fields.every(
        (field) =>
          !find(productAssignments, {
            aaid: field.aaManagedId,
            percent: 0,
          }),
      ),
  ),
};

export const actions = {
  loadBrands,
  createVPIRecommendation,
  createVPISummary,
  setFieldProduct,
  setAssignmentOpen,
  loadAssignments,
  setRecommendationOpen,
  unassignFieldProduct,
  addProduct,
};

function addProduct(aaid, sortOrder) {
  return (dispatch, getState) => {
    const { productAssignments } = getState()[SLICE];

    dispatch({
      type: SET_FIELD_PRODUCT,
      payload: [
        ...productAssignments,
        {
          aaid,
          sortOrder,
          product: '',
          percent: sortOrder === 0 ? 100 : 0,
          crop: 'soy',
        },
      ],
    });
  };
}

function loadBrands() {
  return asyncAction(LOAD_BRANDS, () => api.get(`/product-catalog/brands`));
}

function loadAssignments(growerId, aaids) {
  return asyncAction(LOAD_ASSIGNMENTS, () =>
    api
      .get(
        `/growers/${growerId}/product-placement-vpi/product-summary?ids=${aaids.join(',')}`,
      )
      .then((assignments) =>
        assignments.map((assignment) => ({
          ...assignment,
          aaid: assignment.aaid.toUpperCase(),
        })),
      ),
  );
}

function setAssignmentOpen(isOpen) {
  return {
    type: SET_ASSIGNMENT_OPEN,
    payload: {
      isOpen,
      saveComplete: false,
      hasFailed: null,
      productAssignments: [],
    },
  };
}

function setRecommendationOpen(isOpen) {
  return {
    type: SET_RECOMMENDATION_OPEN,
    payload: { isOpen, saveComplete: false, hasFailed: null },
  };
}

function createVPIRecommendation(growerId, aaids, rmRange, brand) {
  return asyncAction(CREATE_VPI_RECOMMENDATION, async () => {
    const uri = `/growers/${growerId}/product-placement-vpi`;

    await api.post(uri, { aaids, rmRange, brand });
    await pollingAction(() => api.get(`${uri}/status`, { params: { aaids } }));
    await api.downloadVarietyRecommendationPdf(growerId, aaids);
  });
}

function createVPISummary(growerId) {
  return asyncAction(CREATE_VPI_SUMMARY, (_, getState) =>
    api.post(
      `/growers/${growerId}/product-placement-vpi/product-summary`,
      getState()[SLICE].productAssignments,
    ),
  );
}

function setFieldProduct(aaid, product, crop, percent, sortOrder) {
  return (dispatch, getState) => {
    const { productAssignments } = getState()[SLICE];

    const prevAssignments = productAssignments.filter(
      (a) => a.aaid !== aaid || a.sortOrder !== sortOrder,
    );

    dispatch({
      type: SET_FIELD_PRODUCT,
      payload: [
        ...prevAssignments,
        {
          aaid,
          product: product.name,
          seedId: product.id,
          crop,
          percent,
          sortOrder,
        },
      ],
    });
  };
}

function unassignFieldProduct(aaid, sortOrder) {
  return (dispatch, getState) => {
    const { productAssignments } = getState()[SLICE];

    const otherFields = productAssignments.filter(
      (productAssignment) => productAssignment.aaid !== aaid,
    );

    const thisFields = sortBy(productAssignments, 'sortOrder')
      .filter(
        (productAssignment) =>
          productAssignment.aaid === aaid &&
          productAssignment.sortOrder !== sortOrder,
      )
      .map((assignment, i) => ({
        ...assignment,
        sortOrder: i,
      }));

    dispatch({
      type: SET_FIELD_PRODUCT,
      payload: [...otherFields, ...thisFields],
    });
  };
}

const reducerHandlers = {
  [LOAD_BRANDS.start]: (state) => {
    state.loadingBrands = true;
  },
  [LOAD_BRANDS.complete]: (state, payload) => {
    state.brands = payload;
    state.loadingBrands = false;
  },
  [LOAD_BRANDS.failure]: (state) => {
    state.loadingBrands = false;
  },
  [CREATE_VPI_RECOMMENDATION.start]: (state) => {
    state.loading = true;
    state.hasFailed = null;
  },
  [CREATE_VPI_RECOMMENDATION.failure]: (state) => {
    state.hasFailed = 'Unable to create recommendation';
  },
  [CREATE_VPI_RECOMMENDATION.complete]: (state) => {
    state.loading = false;
    state.saveComplete = true;
  },
  [CREATE_VPI_SUMMARY.start]: (state) => {
    state.saving = true;
  },
  [CREATE_VPI_SUMMARY.complete]: (state) => {
    state.saveComplete = true;
    state.saving = false;
  },
  [CREATE_VPI_SUMMARY.failure]: (state) => {
    state.hasFailed = 'Unable to save assignments';
    state.saving = false;
  },
  [SET_FIELD_PRODUCT]: (state, payload) => {
    state.productAssignments = payload;
  },
  [SET_RECOMMENDATION_OPEN]: (state, payload) => {
    const { isOpen, saveComplete, hasFailed } = payload;
    state.isRecommendationOpen = isOpen;
    state.saveComplete = saveComplete;
    state.hasFailed = hasFailed;
  },
  [SET_ASSIGNMENT_OPEN]: (state, payload) => {
    const { isOpen, saveComplete, hasFailed, productAssignments } = payload;

    state.isAssignmentOpen = isOpen;
    state.saveComplete = saveComplete;
    state.hasFailed = hasFailed;
    state.productAssignments = productAssignments;
  },
  [LOAD_ASSIGNMENTS.start]: (state) => {
    state.loading = true;
  },
  [LOAD_ASSIGNMENTS.complete]: (state, payload) => {
    state.productAssignments = payload;
    state.loading = false;
  },
  [LOAD_ASSIGNMENTS.failure]: (state) => {
    state.loading = false;
  },
};

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
